import { useTranslation } from 'next-i18next'
import Head from 'next/head'
import { useRouter } from 'next/router'
import React, { useEffect } from 'react'

import { getSeriesURL } from '../../graphql/learning'
import { usePublicLearningLibrary } from '../../graphql/learning/hooks'
import { LibraryContentQuery } from '../../graphql/learning/api-types'
import { useAuthenticationState } from '../../modules/auth/hooks'
import { useParameter } from '../../url'
import SeriesList from './learning-modules/SeriesList'

const PublicLearningLibraryPage = ({
  library,
  resource,
  resourceID,
  resourceType,
}: {
  library?: LibraryContentQuery & { languages?: string[] }
  resource?: any
  resourceID: string
  resourceType: string
}) => {
  const router = useRouter()
  const libraryID = useParameter('id')
  const { t } = useTranslation()
  const { isAuthenticating } = useAuthenticationState()

  const { data, loading: loadingLibrary } = library
    ? { data: library, loading: false }
    : usePublicLearningLibrary(libraryID)

  useEffect(() => {
    if (!isAuthenticating && !loadingLibrary && !data?.series?.length) {
      void router.push('/learning')
    }
  }, [loadingLibrary, isAuthenticating])

  if (loadingLibrary) {
    return (
      <div
        className="page__container"
        style={{ paddingTop: 20, paddingBottom: 20 }}
      >
        {t('common:ui.activity_indicator')}
        ...
      </div>
    )
  }

  const seriesList = data?.series

  if (seriesList?.length === 0) {
    return (
      <div className="page__container" style={{ paddingTop: 20 }}>
        <div style={{ fontSize: 20 }}>No learning modules</div>
        <div>Please check back again later</div>
      </div>
    )
  }

  const group = resource?.target

  return (
    <div
      className="page__content resource resource--group"
      style={{ paddingTop: 0 }}
    >
      {group ? (
        <Head>
          <title>{group.title}</title>
          <meta property="og:title" content={group.title} />
          {group.description && (
            <meta property="og:description" content={group.description} />
          )}
          {resource?.target?.image_url && (
            <meta
              property="og:image"
              content={group.image_url.replace('{image_size}', 'og-banner')}
            />
          )}
        </Head>
      ) : null}
      <SeriesList
        showDetails
        introHTML={
          resource?.target?.type === 'group'
            ? resource.target.learning_library_intro
            : undefined
        }
        series={seriesList}
        languages={data?.languages ?? []}
        onItemClick={singleSeries => {
          void router.push(getSeriesURL(singleSeries))
        }}
        showGroupFeatured={false}
        resourceType={resourceType}
        resourceID={resourceID}
      />
    </div>
  )
}

export default PublicLearningLibraryPage
